/**
 * The object registry.
 *
 * Every kind of object (Question, Assessment, Blueprint, Submission, Course,
 * Student, Faculty) is defined once: what it is called, which states it can be
 * in and how each reads, how it may move between them, which verbs apply and
 * who may use them, and whether its changes are recorded. A surface asks the
 * registry instead of carrying its own copy, so adding a state, renaming a
 * verb or changing who may publish is one edit.
 */

import type {
  ActionDefinition,
  ObjectDefinition,
  ObjectType,
  Role,
  StatusState,
} from "./types"

const definitions = new Map<ObjectType, ObjectDefinition<any>>()

export function defineObject<T extends { id: string }>(
  definition: ObjectDefinition<T>,
): ObjectDefinition<T> {
  definitions.set(definition.type, definition)
  return definition
}

export function definitionOf<T extends { id: string }>(
  type: ObjectType,
): ObjectDefinition<T> {
  const definition = definitions.get(type)
  if (!definition) throw new Error(`No object definition for "${type}"`)
  return definition as ObjectDefinition<T>
}

export function allDefinitions(): ObjectDefinition<any>[] {
  return [...definitions.values()]
}

const UNKNOWN: StatusState = { label: "Unknown", tone: "neutral", icon: "fa-circle" }

/** The record's state, with its label, tone and icon. Unknown states read neutral, never blank. */
export function statusOf<T extends { id: string }>(
  type: ObjectType,
  record: T,
): { key: string } & StatusState {
  const status = definitionOf<T>(type).status
  if (!status) return { key: "", ...UNKNOWN }
  const key = status.of(record)
  return { key, ...(status.states[key] ?? { ...UNKNOWN, label: key || UNKNOWN.label }) }
}

/** States in lifecycle order, for pickers and filters. */
export function statusOptions(type: ObjectType): Array<{ key: string } & StatusState> {
  const status = definitionOf(type).status
  if (!status) return []
  return Object.entries(status.states).map(([key, state]) => ({ key, ...state }))
}

export function canTransition(type: ObjectType, from: string, to: string): boolean {
  const status = definitionOf(type).status
  if (!status || from === to) return false
  const allowed = status.transitions?.[from]
  return allowed ? allowed.includes(to) : true
}

/** The verbs this role may use on this record, in its current state. */
export function actionsFor<T extends { id: string }>(
  type: ObjectType,
  record: T,
  role: Role,
): ActionDefinition<T>[] {
  return definitionOf<T>(type).actions.filter(
    (action) => action.roles.includes(role) && (action.when?.(record) ?? true),
  )
}

export function can<T extends { id: string }>(
  type: ObjectType,
  actionId: string,
  record: T,
  role: Role,
): boolean {
  return actionsFor(type, record, role).some((action) => action.id === actionId)
}

/** One verb of an object, for surfaces that need its words but not a menu. */
export function verbOf<T extends { id: string }>(
  type: ObjectType,
  actionId: string,
): ActionDefinition<T> {
  const verb = definitionOf<T>(type).actions.find((action) => action.id === actionId)
  if (!verb) throw new Error(`No "${actionId}" verb on ${type}`)
  return verb
}
